import React from 'react';
import { Link } from 'react-router-dom';
import {
    FiArrowRight, FiCamera, FiHeadphones, FiMapPin, FiMessageCircle, FiPackage, FiTruck,
} from 'react-icons/fi';
import "../styles.css";
import "./css/Home.css";
import AnnouncementBanner from './helper/Banner/AnnouncementBanner';
import { isAuthenticated } from '../auth/helper/adminIndex';
import logo from "../images/ttms_logo_1.png";
import fleet from "../images/auth_fleet.webp";
import collage from "../images/auth_collage.webp";

const ROLES = [
    {
        to: '/driverSignin',
        icon: FiTruck,
        title: 'Driver',
        text: "Today's jobs, ticket photos and chat with dispatch — built for the cab.",
    },
    {
        to: '/adminsignin',
        icon: FiHeadphones,
        title: 'Dispatch & admin',
        text: 'Plan loads, watch the fleet live and keep every driver in the loop.',
    },
    {
        to: '/partnerSignin',
        icon: FiPackage,
        title: 'Customer',
        text: 'Track your shipments, see arrival times and pull up tickets anytime.',
    },
];

const FEATURES = [
    {
        icon: FiMapPin,
        title: 'Arrivals check in on their own',
        text: 'Samsara and the driver’s phone mark pickups and drops the moment a truck pulls in.',
    },
    {
        icon: FiCamera,
        title: 'Snap the ticket, skip the typing',
        text: 'Drivers photograph the scale ticket and the numbers fill themselves in.',
    },
    {
        icon: FiMessageCircle,
        title: 'Dispatch is one message away',
        text: 'Chat per driver and per load, with photos, voice notes and read receipts.',
    },
];

// All three portals share one session slot; whichever role is in it decides
// where "Open dashboard" goes.
const dashboardFor = session => {
    if (!session) return null;
    if (session.admin) return '/admin/dashboard';
    if (session.partner) return '/partner/dashboard';
    if (session.driver) return session.driver.appVersion === 'v2' ? '/driver/app' : '/driver/dashboard';
    return null;
};

const scrollToSignin = event => {
    event.preventDefault();
    document.getElementById('signin')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export default function Home() {
    const dashboard = dashboardFor(isAuthenticated());

    return (
        <div className="th">
            <AnnouncementBanner role='global' />

            <header className="th-hero">
                <nav className="th-nav">
                    <Link to="/" className="th-logo">
                        <img src={logo} alt="Triple Trans Mill Services Ltd." />
                    </Link>
                    {dashboard
                        ? <Link to={dashboard} className="th-btn th-btn-ghost">Open dashboard</Link>
                        : <a href="#signin" onClick={scrollToSignin} className="th-btn th-btn-ghost">Sign in</a>}
                </nav>

                <div className="th-hero-copy">
                    <p className="th-eyebrow">Dispatch · Live tracking · Tickets</p>
                    <h1 className="th-title">
                        Every load, every truck, <span>one place.</span>
                    </h1>
                    <p className="th-lede">
                        TTMS runs the day at Triple Trans Mill Services — from the first load
                        offer to the last scale ticket.
                    </p>
                    <div className="th-cta">
                        {dashboard ? (
                            <Link to={dashboard} className="th-btn th-btn-primary">
                                Continue to your dashboard <FiArrowRight size={18} aria-hidden="true" />
                            </Link>
                        ) : (
                            <a href="#signin" onClick={scrollToSignin} className="th-btn th-btn-primary">
                                Sign in <FiArrowRight size={18} aria-hidden="true" />
                            </a>
                        )}
                        <Link to="/driverSignin" className="th-btn th-btn-ghost">
                            <FiTruck size={17} aria-hidden="true" /> Driver app
                        </Link>
                    </div>
                </div>

                <div className="th-hero-art" aria-hidden="true">
                    <img src={fleet} alt="" />
                </div>
            </header>

            <main>
                <section id="signin" className="th-section">
                    <h2 className="th-h2">Sign in to TTMS</h2>
                    <p className="th-section-sub">Choose the account you use.</p>
                    <div className="th-roles">
                        {ROLES.map(({ to, icon: Icon, title, text }) => (
                            <Link key={to} to={to} className="th-role">
                                <span className="th-role-icon"><Icon size={22} aria-hidden="true" /></span>
                                <span className="th-role-title">{title}</span>
                                <span className="th-role-text">{text}</span>
                                <span className="th-role-go">
                                    Sign in <FiArrowRight size={16} aria-hidden="true" />
                                </span>
                            </Link>
                        ))}
                    </div>
                </section>

                <section className="th-section th-features">
                    {FEATURES.map(({ icon: Icon, title, text }) => (
                        <div key={title} className="th-feature">
                            <span className="th-feature-icon"><Icon size={20} aria-hidden="true" /></span>
                            <h3 className="th-feature-title">{title}</h3>
                            <p className="th-feature-text">{text}</p>
                        </div>
                    ))}
                </section>

                <section className="th-section th-photo">
                    <img src={collage} alt="Triple Trans trucks at the yard and on the road" loading="lazy" />
                </section>
            </main>

            <footer className="th-footer">
                <img src={logo} alt="Triple Trans Mill Services Ltd." className="th-footer-logo" />
                <div className="th-footer-links">
                    <a href="mailto:admin@tripletrans.ca">admin@tripletrans.ca</a>
                    <span>© {new Date().getFullYear()} Triple Trans Mill Services Ltd.</span>
                    <span>Built by <a href="mailto:admin@devify.ca">Developreneurs Tech Limited</a></span>
                </div>
            </footer>
        </div>
    );
}
